/**
 * What the timesheet list (/payroll/approvals) and one timesheet's own page
 * (/payroll/approvals/:timesheetId) both need to agree on.
 *
 * The list used to put the chosen shift in a panel beside it. Split panes are
 * banned (CLAUDE.md), so a row now opens its own page — and that page still has
 * to know the list the reader came from, because an approve moves on to the
 * NEXT row of that list (fe#750) and reports how many are left in its tab. So
 * the list's narrowing (status tab, date chip, name search) lives in the URL,
 * and both screens derive the same rows from the same query with the functions
 * below. One definition, two callers: a second copy of the filter on the record
 * page would be a second answer to "which row is next".
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import { calendarDayString, parseCalendarDate, tenantTodayDate, todayCalendarDate } from '../../lib/calendar-date';
import { isFlaggedShift } from '../../lib/long-shift';
import { shiftHoursText } from '../../lib/shift-hours';

export const QUEUE_PATH = '/payroll/approvals';

/* ── Types ── */
export interface Timesheet {
  id: string;
  /**
   * The worker. `timesheets` has NO `user_id` column — this interface declared
   * one, the list SELECT is `t.*`, and so `user_id` was `undefined` on every row
   * ever rendered here. Nothing read it, which is the only reason it never
   * surfaced. Verified against information_schema, not assumed.
   */
  employee_id: string;
  worker_name: string;
  worker_initials?: string;
  entry_date: string;
  project?: string;
  /** Snapshotted on a work-item shift (e.g. EWJ-0001); read via shiftJobNumber. */
  job_number?: string | null;
  /** The API's resolved number, the job's own for a casing shift; read via shiftJobNumber. */
  display_job_number?: string | null;
  /** 'shop' | 'field' | null (api 0333). NULL means nobody said — render '—'. */
  work_location?: string | null;
  /**
   * `draft` is the status a row is BORN in — the clock-in INSERT writes it — and
   * it covers two states this screen has to keep apart, because `clock_out_at`
   * is the only thing that separates them:
   *
   *   draft + clock_out_at NULL      → the shift is still running. On the clock.
   *   draft + clock_out_at NOT NULL  → clocked out, never handed in. Unsubmitted.
   *
   * Since #594 clock-out submits the row on the way through, so the second of
   * those is a leftover, not a stage anyone passes through.
   */
  status: 'draft' | 'submitted' | 'flagged' | 'approved' | 'rejected';
  clock_in_at: string;
  clock_out_at: string | null;
  /** Who handed it in. NULL until submit; the approve route bars this person. */
  submitted_by?: string | null;
  raw_minutes: number;
  /**
   * Minutes between the ROUNDED punches (api#823). NULL on every shift closed
   * while rounding was off, which means "use raw_minutes". Read only through
   * shiftHoursText / minutesOfRecord.
   */
  payable_minutes?: number | string | null;
  regular_hours: number;
  overtime_hours: number;
  doubletime_hours?: number | null;
  shift_type?: string;
  standby_hours?: number;
  clock_in_lat?: number | null;
  /**
   * `text[]` on the server, one `code: reason` element per flag — api#992's
   * `long_shift: 30.15h on the clock, …` is one. Typed `string` here for years,
   * which rendered an array as its elements run together. Read only through
   * lib/long-shift.ts.
   */
  anomaly_flags?: string[] | string | null;
  /** The API's long-shift verdict (api#1010). Read only through lib/long-shift.ts. */
  is_long_shift?: boolean;
  note?: string;
  photos?: string[];
  /**
   * api #595: the worker moved this row AFTER handing it in and before anyone
   * signed for it. Derived from the audit trail on the server, never stored;
   * absent on an older API and then simply not shown.
   */
  edited_after_submission?: boolean;
  /** 'manual' = a missed shift the worker entered by hand, no punch (api#1148). */
  clock_source?: string | null;
  /** Why an approved shift has no pay entry (api#1148). Read only through lib/pay-gap. */
  pay_gap?: string | null;
  pay_gap_message?: string | null;
}

/**
 * The four ACTIONABLE tabs, in the order a shift moves through them. Each one
 * is a `timesheets.status` except `unsubmitted`, which is `draft` narrowed to
 * the rows that have clocked out — see the Timesheet.status docblock.
 */
export type StatusTab = 'unsubmitted' | 'submitted' | 'approved' | 'rejected';

/**
 * `on_the_clock` is deliberately NOT a StatusTab. It is not a queue: an open
 * shift has no end time, so there is nothing to submit and nothing to sign for,
 * and every action on this screen is refused on it by the server as a matter of
 * shape rather than permission. Keeping it out of the union is what stops a
 * later `statusTab === x` branch from quietly handing it an Approve button.
 */
export type QueueTab = StatusTab | 'on_the_clock';

export type DateFilter = 'all' | 'today' | 'week' | 'flagged';

const QUEUE_TABS: QueueTab[] = ['unsubmitted', 'submitted', 'approved', 'rejected', 'on_the_clock'];
const DATE_FILTERS: DateFilter[] = ['all', 'today', 'week', 'flagged'];

/**
 * The list's narrowing, as the URL carries it: `?status=`, `?date=`, `?q=`.
 * The defaults — Pending, every date, no name — are written as ABSENT params,
 * so the bare /payroll/approvals the nav links to is the default list and
 * nothing has to be stamped into a URL to get there.
 *
 * An unrecognised value falls back to the default rather than being trusted:
 * a hand-edited `?status=archived` would otherwise filter to nothing and read
 * as an empty queue. `flagged` only exists on Pending, so it is dropped on any
 * other tab, which is what the date chips offer there.
 */
export interface QueueParams {
  status: QueueTab;
  date: DateFilter;
  q: string;
}

export function readQueueParams(params: URLSearchParams): QueueParams {
  const s = params.get('status');
  const status: QueueTab = QUEUE_TABS.includes(s as QueueTab) ? (s as QueueTab) : 'submitted';
  const d = params.get('date');
  let date: DateFilter = DATE_FILTERS.includes(d as DateFilter) ? (d as DateFilter) : 'all';
  if (date === 'flagged' && status !== 'submitted') date = 'all';
  return { status, date, q: params.get('q') ?? '' };
}

/** Writes `next` over `params`, leaving any other key (the view, the report) alone. */
export function writeQueueParams(params: URLSearchParams, next: QueueParams): URLSearchParams {
  const out = new URLSearchParams(params);
  if (next.status === 'submitted') out.delete('status'); else out.set('status', next.status);
  if (next.date === 'all') out.delete('date'); else out.set('date', next.date);
  if (next.q === '') out.delete('q'); else out.set('q', next.q);
  return out;
}

/** "Today" and "this week" are the TENANT's (companies.time_zone), not the device's. No zone, no match. */
export function isTenantToday(dateStr: string, timeZone: string | null): boolean {
  const todayStr = todayCalendarDate(timeZone);
  return todayStr !== null && calendarDayString(dateStr) === todayStr;
}

export function isTenantThisWeek(dateStr: string, timeZone: string | null): boolean {
  // Appending 'T00:00:00' worked only on a bare 'YYYY-MM-DD'. On either longer
  // spelling ('…T00:00:00.000Z' or '… 00:00:00+00') it built Invalid Date, and
  // the entry dropped out of "This week" (fe#815).
  const d = parseCalendarDate(dateStr);
  if (!d) return false;
  // The tenant's today as a local-midnight Date, so getDay() is the tenant's weekday.
  const startOfWeek = tenantTodayDate(timeZone);
  if (!startOfWeek) return false;
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  return d >= startOfWeek;
}

/** Clocked out, never handed in. See the Timesheet.status docblock. */
export const isUnsubmitted = (t: Timesheet) => t.status === 'draft' && !!t.clock_out_at;

/**
 * The tab, then the date chip — and SEPARATELY the name search.
 *
 * `tabRows` is what is in the tab. `rows` is what the reader is looking at.
 * Everything that makes a CLAIM ABOUT THE QUEUE — the "N remaining" an approve
 * reports — reads `tabRows`, so a search for one name cannot make the screen
 * say the queue is empty when it is one person's shifts that are. The chip
 * counts and the band's figures are narrower still: they read the whole list
 * and ignore both filters.
 *
 * The name match is trimmed, case-folded, substring. Not initials and not
 * fuzzy: a match the reader cannot predict is worse than one they have to type
 * in full, because the failure mode of a clever matcher is a row that is
 * silently not there.
 *
 * On the clock returns nothing here: it is a board of open shifts, not a list
 * of rows to open, and the list renders it from its own split.
 */
export function queueRows(all: Timesheet[], p: QueueParams, timeZone: string | null): { tabRows: Timesheet[]; rows: Timesheet[] } {
  if (p.status === 'on_the_clock') return { tabRows: [], rows: [] };
  // `unsubmitted` is not a status, so it cannot be compared to one. Every
  // other tab is the status it is named for.
  let tabRows = p.status === 'unsubmitted' ? all.filter(isUnsubmitted) : all.filter((t) => t.status === p.status);
  if (p.date === 'today') tabRows = tabRows.filter((t) => isTenantToday(t.entry_date, timeZone));
  if (p.date === 'week') tabRows = tabRows.filter((t) => isTenantThisWeek(t.entry_date, timeZone));
  if (p.date === 'flagged') tabRows = tabRows.filter(isFlaggedShift);
  const q = p.q.trim().toLowerCase();
  const rows = q === '' ? tabRows : tabRows.filter((t) => (t.worker_name ?? '').toLowerCase().includes(q));
  return { tabRows, rows };
}

/**
 * WHERE THE READER GOES AFTER A ROW IS SIGNED FOR — THE ROW BELOW IT, NOT THE
 * TOP OF THE LIST (fe#750).
 *
 * POSITIONAL, over the flat, API-ordered rows the list draws its groups from,
 * so "the next one" is the next row on screen: the next shift inside a
 * person's group, or the first shift of the group below.
 *
 * THE LAST ROW FALLS BACK TO THE PREVIOUS ONE, not to the top and not to
 * nothing. At the end of a queue the row above is what the reader was just
 * working through; the top is somewhere they have already been.
 *
 * THE EDGES:
 *   - last row  → `[i + 1]` is undefined → the row above.
 *   - only row  → neither neighbour exists → null. Nothing is left to sign,
 *                 and the record page returns to the list.
 *   - not found → `findIndex` returns -1, so `[i + 1]` is `[0]`: the top of
 *                 the list. A row that is no longer in the list has no
 *                 position to be "after", and this falls out of the arithmetic
 *                 rather than needing a branch. An explicit `i === -1` guard
 *                 was written here once and removed: two guards for one rule,
 *                 and no mutation of either alone can change the result.
 *
 * `rows[i - 1]` at `i === 0` reads `rows[-1]`, which on a JS array is
 * `undefined` rather than the last element — deliberately NOT `.at(i - 1)`,
 * which would wrap.
 */
export function rowAfter(rows: Timesheet[], excludeId: string): Timesheet | null {
  const i = rows.findIndex((t) => t.id === excludeId);
  return rows[i + 1] ?? rows[i - 1] ?? null;
}

/**
 * ONE request, unfiltered, shared by the list and the record page (same key,
 * one cache entry). See the list screen for why it is not split server-side.
 */
export function useTimesheetQueue() {
  return useQuery({
    queryKey: ['timesheets', 'all'],
    queryFn: () => api.timesheets.list({}).then((r) => r.data?.data ?? r.data ?? []),
    refetchInterval: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Recorded shift length, for display: "8.00h", "7.25h (clocked 7.30h)" when a
 * clock-rounding snapshot differs from the punches, or an em dash when the row
 * has none. A real zero is NOT absence and still prints 0.00h.
 */
export function hoursText(ts: Pick<Timesheet, 'payable_minutes' | 'raw_minutes'> | undefined | null): string {
  return shiftHoursText(ts?.payable_minutes, ts?.raw_minutes);
}

/**
 * Mirrors TIMESHEET_APPROVER_ROLES in the API's timesheets.service.ts. The
 * approve and reject routes are `requireRole(...)` NON-strict, so a dispatcher
 * ALSO passes on a tenant that has turned on `dispatcher_admin_access` — see
 * `useApproverAccess` for why that flag is worth a query here.
 */
export const APPROVER_ROLES = ['admin', 'office_staff', 'supervisor'];

/**
 * Who may approve, and who may submit on someone else's behalf. Two answers,
 * and they are NOT the same list.
 *
 * `canApprove` DIVERGES from src/lib/roles.ts deliberately. That file
 * documents the opposite choice for the forms routes — gate on the literal
 * allow list and let an elevated dispatcher be under-granted rather than spend
 * a query. DO NOT CONVERGE THEM. Approval is two-party: on Dash Energy the
 * dispatcher IS the second approver (1 admin + 1 elevated dispatcher is the
 * entire eligible pair). Hiding the button from them would leave that tenant
 * unable to approve ANY timesheet, and an unapproved timesheet never enters the
 * safety exposure denominator.
 *
 * `dispatcher_admin_access` is NOT on the /auth/me payload, so it is read from
 * GET /settings/roles, which any authenticated user in the company may read.
 * Same key as RolesPermissionsPage so the two share a cache entry. Undefined
 * while in flight, which starts the button hidden and lets it appear.
 *
 * `canSubmitForOthers` does NOT reuse `canApprove`. Submit has no route gate;
 * `submitTimesheet`'s on-behalf branch calls the STRICT `isApproverRole`, which
 * does not consult `dispatcher_admin_access`. Showing an elevated dispatcher a
 * Submit button would be a click into a 403 — see timesheets.service.ts.
 */
export function useApproverAccess() {
  const { user } = useAuth();
  const { data: rolesSettings } = useQuery({
    queryKey: ['settings', 'roles'],
    queryFn: async () => {
      const res = await api.settingsRoles.get();
      return (res.data?.data ?? res.data ?? {}) as { dispatcher_admin_access?: boolean };
    },
  });
  const canApprove = useMemo(() => {
    const role = user?.role;
    if (!role) return false;
    if (APPROVER_ROLES.includes(role)) return true;
    return role === 'dispatcher' && rolesSettings?.dispatcher_admin_access === true;
  }, [user?.role, rolesSettings]);
  const canSubmitForOthers = !!user?.role && APPROVER_ROLES.includes(user.role);
  return { canApprove, canSubmitForOthers };
}

/**
 * The two-party attestation conflicts from POST /timesheets/:id/approve, added
 * by API PR #353. The server's own messages are accurate but written for an API
 * consumer; these say the same thing in the words an approver standing at the
 * queue would use. Anything else — including the 403 from requireRole — falls
 * through to the server message.
 *
 * Approve only. `rejectTimesheet` has no self- or submitter-bar by design
 * (rejecting is not an attestation), so neither code can reach a reject.
 */
export const APPROVAL_CONFLICT_MESSAGES: Record<string, string> = {
  self_approval:
    "You can't approve your own timesheet — a second person has to sign for these hours.",
  submitter_approval:
    'You submitted this one, so someone else has to approve it.',
};
